import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowRight,
  Bot,
  Headphones,
  Megaphone,
  Mic,
  Sparkles,
  Zap
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const modules = [
  {
    id: 'ai_sales_automation',
    name: 'AI Sales Automation',
    description: 'Automate your sales pipeline with AI agents, follow-ups, proposals, workflows and customer engagement.',
    status: 'Active',
    icon: Zap,
    action: 'Open',
    enabled: true,
    path: '/dashboard'
  },
  {
    id: 'ai_lead_generation',
    name: 'AI Lead Generation',
    description: 'Find and qualify new prospects with AI-assisted lead discovery workflows.',
    status: 'Coming Soon',
    icon: Sparkles,
    action: 'Available Soon',
    enabled: false
  },
  {
    id: 'ai_customer_support',
    name: 'AI Customer Support',
    description: 'Automated customer support workflows for tickets, replies, and service quality.',
    status: 'Planned',
    icon: Headphones,
    action: 'Planned',
    enabled: false
  },
  {
    id: 'ai_marketing',
    name: 'AI Marketing',
    description: 'Campaign planning, content assistance, and marketing automation modules.',
    status: 'Planned',
    icon: Megaphone,
    action: 'Planned',
    enabled: false
  },
  {
    id: 'ai_voice_agent',
    name: 'AI Voice Agent',
    description: 'Voice-based AI workflows for calls, qualification, and customer conversations.',
    status: 'Planned',
    icon: Mic,
    action: 'Planned',
    enabled: false
  }
];

const statusClasses = {
  Active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Coming Soon': 'bg-amber-100 text-amber-700 border-amber-200',
  Planned: 'bg-slate-100 text-slate-600 border-slate-200'
};

export default function PlatformDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const welcomeName = user?.name ? `, ${user.name}` : '';

  return (
    <div className="p-8" data-testid="platform-dashboard">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-lg bg-blue-600 flex items-center justify-center">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">EmpowerBiz AI</h1>
            <p className="text-slate-500">AI Business Growth Platform</p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-slate-900">
          Welcome back{welcomeName}
        </h2>
        <p className="text-slate-500 mt-1">
          Choose the AI module you'd like to use today.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
        {modules.map((module) => {
          const Icon = module.icon;
          return (
            <Card key={module.id} className="border-slate-200 shadow-sm">
              <CardContent className="p-6 flex flex-col min-h-[260px]">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div className="w-11 h-11 rounded-lg bg-slate-900 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <Badge className={statusClasses[module.status]}>
                    {module.status}
                  </Badge>
                </div>

                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-slate-900 mb-2">
                    {module.name}
                  </h2>
                  <p className="text-sm text-slate-500 leading-6">
                    {module.description}
                  </p>
                </div>

                <Button
                  className="mt-6 gap-2"
                  variant={module.enabled ? 'default' : 'outline'}
                  disabled={!module.enabled}
                  onClick={() => module.path && navigate(module.path)}
                  data-testid={`module-${module.id}`}
                >
                  {module.action}
                  {module.enabled && <ArrowRight className="w-4 h-4" />}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
